/*!

=========================================================
* Argon Design System React - v1.1.2
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-design-system-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-design-system-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useEffect, useRef, useState } from "react";
// nodejs library that concatenates classes
import classnames from "classnames";

// reactstrap components
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardImg,
  FormGroup,
  Input,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  Container,
  Row,
  Col,
  Progress,
  Nav,
  NavItem,
  TabContent,
  TabPane,
  CardTitle,
  CardText,
  CardSubtitle
} from "reactstrap";

import "./Courses.css";
import useReveal from "hooks/useReveal";
import logoVideo from "../../assets/videos/loadingCircleBlackOnWhite1080.mp4";

// core components
import DemoNavbar from "components/Navbars/DemoNavbar.js";
import MainFooter from "components/Footers/MainFooter.js";

// index page sections
import Download from "../IndexSections/Download.js";

import { FaCheckCircle, FaCheck } from "react-icons/fa";
import { Link } from "react-router-dom";
import { getAllRegisteredCourses } from "../../services/userService.js";

import { registerToCourse } from "../../services/userService.js";
import { useUserAuth } from "../../context/UserContext";
import { useTranslation, Trans } from "react-i18next";
{/* CODE BELLOW IS ANOTHER WAY TO DISPLAY IMAGES. DO NOT REMOVE UNTIL APPLICATION IS FULLED TESTED SINCE THE CURRENT METHOD IS UNSTABLE */ }
// const coursesLogos = require.context('../../assets/img/icons/common', false, /\.(png|jpe?g|svg)$/);


const Courses = () => {
  const mainRef = useRef(null);
  const { user } = useUserAuth();
  const [myCourses, setMyCourses] = useState(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const { t, i18n } = useTranslation();
  const tabRefs = useRef({});

  const [search, setSearch] = useState("");


  const [coursesLogosFiles, setCoursesLogosFiles] = useState({
    JA01: "JavaCourseLogo.png",
    JS01: "JavaScriptCourseLogo2.png",
    CC01: "ClaudeAICourse.png",
    CD01: "ClaudeAICourse.png",
    CD02: "ClaudeAICourse.png",
    CD03: "ClaudeAICourse.png",
    CD04: "ClaudeAICourse.png"
  });

  const [coursesTableLinks, setCoursesTableLinks] = useState({
    JA01: "java-logo.png",
    JS01: "javascript-table",
    CC01: "CC_M1_L1",
    CD01: "claudedev-table",
    CD02: "claudedev-v2-table",
    CD03: "claudedev-v3-table",
    CD04: "claudedev-v4-table"
  });

  // 🚩 PIVOT — shelved, not deleted. The catalog shows ONLY the "Claude AI
  // Developer" (CD01) course. The "Use Claude Like a Developer" (CC01),
  // JavaScript, Java, and Python cards — and any other registered course shown
  // under "My Courses" — are hidden behind this flag. Flip to `true` to bring
  // them all back exactly as before.
  const SHOW_LEGACY_COURSES = false;

  // "Claude AI Developer V2" (CD02) is the build-your-app-during-the-course
  // rewrite. It's now open to all users and shown alongside CD01. Its routes are
  // gated like CD01 in App.js — table of contents and first lesson public, the
  // rest behind an active subscription or the one-time course purchase.
  const showV2Course = true;

  // "Claude AI Developer V3" (CD03) rebuilds CD02's Module 3 around reading and
  // directing code instead of writing it, so a complete beginner isn't handed a
  // syntax curriculum. A SEPARATE course — CD02 stays live and untouched beside it.
  // Gated identically in App.js: table of contents and first lesson public, the
  // rest behind an active subscription or the one-time course purchase.
  const showV3Course = true;

  // "Claude AI Developer V4" (CD04) is the final cut: 43 lessons chosen by hand out
  // of CD01, CD02 and CD03, with the Claude Code module moved up so the student
  // leaves the browser before shipping instead of after. A SEPARATE course again —
  // the other three stay live and untouched beside it. Gated identically in App.js:
  // table of contents and first lesson public, the rest behind an active
  // subscription or the one-time course purchase.
  const showV4Course = true;


  useEffect(() => {
    fetchRegisteredCourses();
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
    }

    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, []);

  const callRegisterToCourse = async (courseID) => {
    setIsRegistering(true);

    try {
      await registerToCourse(courseID, user);
      await fetchRegisteredCourses();
    } catch (error) {
      console.log("Register failed:", error);
    } finally {
      setIsRegistering(false);
    }
  };

  const [activeTab, setActiveTab] = useState("1");

  const toggle = (tab) => {
    if (activeTab !== tab) {
      fetchRegisteredCourses();
      setActiveTab(tab);
    }
  };

  // "My Courses" is a tab only for a signed-in student.
  const courseTabs = [
    { id: "1", name: "catalog", label: t("courses.register") },
    ...(user ? [{ id: "2", name: "mine", label: t("courses.myCourses") }] : []),
  ];

  // Arrow keys, Home and End move between the tabs (and select them).
  const onTabKeyDown = (e) => {
    const ids = courseTabs.map((tab) => tab.id);
    const i = Math.max(ids.indexOf(activeTab), 0);
    const next = {
      ArrowRight: ids[(i + 1) % ids.length],
      ArrowLeft: ids[(i - 1 + ids.length) % ids.length],
      Home: ids[0],
      End: ids[ids.length - 1],
    }[e.key];
    if (!next) return;
    e.preventDefault();
    toggle(next);
    tabRefs.current[next]?.focus();
  };

  // Course names, for the "My Courses" cards, which show only the course art.
  const courseTitle = (courseID) => ({
    CC01: t("courses.cc01Title"),
    CD01: t("table.claudeDevTableT"),
    CD02: t("table.claudeDevV2TableT"),
    CD03: t("courses.cd03Title"),
    CD04: t("courses.cd04Title"),
    JS01: "JavaScript",
    JA01: "Java",
  })[courseID] || courseID;

  // test getAllRegisteredCourses

  const fetchRegisteredCourses = async () => {
    let allCourses = await getAllRegisteredCourses(user);
    setMyCourses(allCourses);

  };


  const isCourseRegistered = (courseID) => {
    return Array.isArray(myCourses) && myCourses.includes(courseID);
  };

  // Scroll reveal. The band and the catalog cards exist from the first paint; the
  // "My Courses" cards only appear once the registered list loads, so a second
  // scan runs whenever that list changes. Two calls rather than one with deps: a
  // re-scan releases anything still waiting, which would skip the catalog's
  // entrance on a phone, where most of its cards start below the fold.
  useReveal();
  useReveal([myCourses]);

  return (
    <>
      <DemoNavbar />
      <main ref={mainRef} className="courses-page">
        {isRegistering && (
          <div className="courses-loading-overlay" role="alert" aria-busy="true" aria-live="polite">
            <div className="courses-loading-card">
              <video
                className="courses-loading-video"
                src={logoVideo}
                autoPlay
                loop
                muted
                playsInline
                draggable={false}
                aria-hidden="true"
              />
              {/* <img
                className="courses-loading-img"
                src={require("assets/img/theme/loading.jpg")}
                alt="Loading..."
              /> */}
              <div className="courses-loading-text">{t("courses.registering")}</div>
            </div>
          </div>
        )}

        {/* The navy band: page title and the tabs. DemoNavbar stays solid and in
            normal flow above it, so there is no transparent-navbar override and no
            body class to add here. */}
        <section className="courses-hero">
          <span className="cn-grain" aria-hidden="true" />
          <span className="courses-orb courses-orb-1" aria-hidden="true" />
          <span className="courses-orb courses-orb-2" aria-hidden="true" />
          <Container className="container-lg">
            <h1 className="courses-title" id="courses-title" data-reveal>{t("nav.courses")}</h1>
            <div data-reveal style={{ "--reveal-delay": "90ms" }}>
              <Nav tabs className="courses-tabs" role="tablist" aria-labelledby="courses-title" onKeyDown={onTabKeyDown}>
                {courseTabs.map((tab) => (
                  <NavItem key={tab.id} role="presentation">
                    <button
                      type="button"
                      role="tab"
                      id={`courses-tab-${tab.name}`}
                      className={classnames("nav-link", { active: activeTab === tab.id })}
                      aria-selected={activeTab === tab.id}
                      aria-controls={`courses-pane-${tab.name}`}
                      tabIndex={activeTab === tab.id ? 0 : -1}
                      ref={(el) => { tabRefs.current[tab.id] = el; }}
                      onClick={() => toggle(tab.id)}
                    >
                      {tab.label}
                    </button>
                  </NavItem>
                ))}
              </Nav>
            </div>
          </Container>
        </section>

        <section className="courses-body">
          <Container className="container-lg">
            <TabContent activeTab={activeTab}>
              <TabPane tabId="1" role="tabpanel" id="courses-pane-catalog" aria-labelledby="courses-tab-catalog">
                <section className="courses-pane">
                  <Row className="justify-content mb-4">
                    <Col xs="12" md="8" lg="6">
                      {/* <InputGroup>
                        <InputGroupText>

                        </InputGroupText>
                        <Input
                          placeholder="Search courses…"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                        />
                      </InputGroup> */}
                    </Col>
                  </Row>
                  {/* Each course below is its own Row; .courses-grid lays those Rows
                      out as a two-column grid of equal-height cards. */}
                  <div className="courses-grid">
                  {/* Claude Course — Use Claude Like a Developer (SHELVED behind SHOW_LEGACY_COURSES) */}
                  {SHOW_LEGACY_COURSES && (
                  <Row className="" data-reveal>
                    <Col>
                      <Card className="course-card">
                        <Row className="course-card-row">
                          <Col className="col-3 course-logo-col">
                            <Link to="/CC_M1_L1">
                              <CardImg className="course-logo" alt={t("courses.cc01Title")} src={require("assets/img/icons/common/ClaudeAICourse.png")} />
                            </Link>
                          </Col>
                          <Col className="col-9">
                            <CardBody>
                              <CardTitle tag="h2">
                                {t("courses.cc01Title")}
                              </CardTitle>
                              <CardText>
                                {t("courses.cc01Description")}
                              </CardText>
                              <Row className="courses-card-foot">
                                {isCourseRegistered("CC01") ? (
                                  <>
                                    <Col xs="6">
                                      <CardText color="success" className="courses-registered">
                                        <FaCheck className="courses-registered-icon" />
                                        {t("courses.registered")}
                                      </CardText>
                                    </Col>
                                    <Col xs="6">
                                      <Link className="courses-go" to="/CC_M1_L1">
                                        <small>{t("courses.goToCourse")}</small>
                                        <span className="sr-only">{t("courses.cc01Title")}</span>
                                      </Link>
                                    </Col>
                                  </>
                                ) : (
                                  <Button className="courses-btn" color="primary" type="button" onClick={() => callRegisterToCourse("CC01")}>
                                    {t("courses.register")}
                                  </Button>
                                )}
                              </Row>
                            </CardBody>
                          </Col>
                        </Row>
                      </Card>
                    </Col>
                  </Row>
                  )}

                  {/* Claude AI Developer */}
                  <Row className="" data-reveal>
                    <Col>
                      <Card className="course-card">
                        <Row className="course-card-row">
                          <Col className="col-3 course-logo-col">
                            <Link to="/claudedev-table">
                              <CardImg className="course-logo" alt={t("table.claudeDevTableT")} src={require("assets/img/icons/common/ClaudeAICourse.png")} />
                            </Link>
                          </Col>
                          <Col className="col-9">
                            <CardBody>
                              <CardTitle tag="h2">
                                {t("table.claudeDevTableT")}
                              </CardTitle>
                              <CardText>
                                {t("courses.cd01Description")}
                              </CardText>
                              <Row className="courses-card-foot">
                                {isCourseRegistered("CD01") ? (
                                  <>
                                    <Col xs="6">
                                      <CardText color="success" className="courses-registered">
                                        <FaCheck className="courses-registered-icon" />
                                        {t("courses.registered")}
                                      </CardText>
                                    </Col>
                                    <Col xs="6">
                                      <Link className="courses-go" to="/claudedev-table">
                                        <small>{t("courses.goToCourse")}</small>
                                        <span className="sr-only">{t("table.claudeDevTableT")}</span>
                                      </Link>
                                    </Col>
                                  </>
                                ) : (
                                  <Button className="courses-btn" color="primary" type="button" onClick={() => callRegisterToCourse("CD01")}>
                                    {t("courses.register")}
                                  </Button>
                                )}
                              </Row>
                            </CardBody>
                          </Col>
                        </Row>
                      </Card>
                    </Col>
                  </Row>

                  {/* Claude AI Developer V2 — open to all users (see showV2Course above) */}
                  {showV2Course && (
                  <Row className="" data-reveal>
                    <Col>
                      <Card className="course-card">
                        <Row className="course-card-row">
                          <Col className="col-3 course-logo-col">
                            <Link to="/claudedev-v2-table">
                              <CardImg className="course-logo" alt={t("table.claudeDevV2TableT")} src={require("assets/img/icons/common/ClaudeAICourse.png")} />
                            </Link>
                          </Col>
                          <Col className="col-9">
                            <CardBody>
                              <CardTitle tag="h2">
                                {t("table.claudeDevV2TableT")} <span className="badge courses-badge">{t("courses.testingBadge")}</span>
                              </CardTitle>
                              <CardText>
                                {t("courses.cd02Description")}
                              </CardText>
                              <Row className="courses-card-foot">
                                {isCourseRegistered("CD02") ? (
                                  <>
                                    <Col xs="6">
                                      <CardText color="success" className="courses-registered">
                                        <FaCheck className="courses-registered-icon" />
                                        {t("courses.registered")}
                                      </CardText>
                                    </Col>
                                    <Col xs="6">
                                      <Link className="courses-go" to="/claudedev-v2-table">
                                        <small>{t("courses.goToCourse")}</small>
                                        <span className="sr-only">{t("table.claudeDevV2TableT")}</span>
                                      </Link>
                                    </Col>
                                  </>
                                ) : (
                                  <Button className="courses-btn" color="primary" type="button" onClick={() => callRegisterToCourse("CD02")}>
                                    {t("courses.register")}
                                  </Button>
                                )}
                              </Row>
                            </CardBody>
                          </Col>
                        </Row>
                      </Card>
                    </Col>
                  </Row>
                  )}

                  {/* Claude AI Developer V3 — open to all users (see showV3Course above) */}
                  {showV3Course && (
                  <Row className="" data-reveal>
                    <Col>
                      <Card className="course-card">
                        <Row className="course-card-row">
                          <Col className="col-3 course-logo-col">
                            <Link to="/claudedev-v3-table">
                              <CardImg className="course-logo" alt={t("courses.cd03Title")} src={require("assets/img/icons/common/ClaudeAICourse.png")} />
                            </Link>
                          </Col>
                          <Col className="col-9">
                            <CardBody>
                              <CardTitle tag="h2">
                                {t("courses.cd03Title")}
                              </CardTitle>
                              <CardText>
                                {t("courses.cd03Description")}
                              </CardText>
                              <Row className="courses-card-foot">
                                {isCourseRegistered("CD03") ? (
                                  <>
                                    <Col xs="6">
                                      <CardText color="success" className="courses-registered">
                                        <FaCheck className="courses-registered-icon" />
                                        {t("courses.registered")}
                                      </CardText>
                                    </Col>
                                    <Col xs="6">
                                      <Link className="courses-go" to="/claudedev-v3-table">
                                        <small>{t("courses.goToCourse")}</small>
                                        <span className="sr-only">{t("courses.cd03Title")}</span>
                                      </Link>
                                    </Col>
                                  </>
                                ) : (
                                  <Button className="courses-btn" color="primary" type="button" onClick={() => callRegisterToCourse("CD03")}>
                                    {t("courses.register")}
                                  </Button>
                                )}
                              </Row>
                            </CardBody>
                          </Col>
                        </Row>
                      </Card>
                    </Col>
                  </Row>
                  )}

                  {/* Claude AI Developer V4 — open to all users (see showV4Course above) */}
                  {showV4Course && (
                  <Row className="" data-reveal>
                    <Col>
                      <Card className="course-card">
                        <Row className="course-card-row">
                          <Col className="col-3 course-logo-col">
                            <Link to="/claudedev-v4-table">
                              <CardImg className="course-logo" alt={t("courses.cd04Title")} src={require("assets/img/icons/common/ClaudeAICourse.png")} />
                            </Link>
                          </Col>
                          <Col className="col-9">
                            <CardBody>
                              <CardTitle tag="h2">
                                {t("courses.cd04Title")}
                              </CardTitle>
                              <CardText>
                                {t("courses.cd04Description")}
                              </CardText>
                              <Row className="courses-card-foot">
                                {isCourseRegistered("CD04") ? (
                                  <>
                                    <Col xs="6">
                                      <CardText color="success" className="courses-registered">
                                        <FaCheck className="courses-registered-icon" />
                                        {t("courses.registered")}
                                      </CardText>
                                    </Col>
                                    <Col xs="6">
                                      <Link className="courses-go" to="/claudedev-v4-table">
                                        <small>{t("courses.goToCourse")}</small>
                                        <span className="sr-only">{t("courses.cd04Title")}</span>
                                      </Link>
                                    </Col>
                                  </>
                                ) : (
                                  <Button className="courses-btn" color="primary" type="button" onClick={() => callRegisterToCourse("CD04")}>
                                    {t("courses.register")}
                                  </Button>
                                )}
                              </Row>
                            </CardBody>
                          </Col>
                        </Row>
                      </Card>
                    </Col>
                  </Row>
                  )}

                  {SHOW_LEGACY_COURSES && (<>
                  <Row className="" data-reveal>
                    <Col>
                      <Card className="course-card">
                        <Row className="course-card-row">
                          <Col className="col-3 course-logo-col">
                            <Link to="/landing-page">
                              <CardImg className="course-logo" alt="JavaScript" src={require("assets/img/icons/common/JavaScriptCourseLogo2.png")} />
                            </Link>
                          </Col>
                          <Col className="col-9">
                            <CardBody>
                              <CardTitle tag="h2">
                                JavaScript
                              </CardTitle>
                              {/* <CardSubtitle className="mb-2 text-muted" tag="h6">
                                Card subtitle
                              </CardSubtitle> */}
                              <CardText>
                                {t("courses.javascriptDescription")}
                              </CardText>
                              {/* <Button className="courses-btn" color="primary" type="button" onClick={() => callRegisterToCourse("JS01")}>
                            Register
                          </Button> */}
                              <Row className="courses-card-foot">


                                {isCourseRegistered("JS01") ? (
                                  <>
                                    <Col xs="6">
                                      <CardText color="success" className="courses-registered">

                                        {/* <FaCheckCircle /> */}
                                        <FaCheck className="courses-registered-icon" />
                                        {t("courses.registered")}

                                      </CardText>
                                    </Col>
                                    <Col xs="6">
                                      <Link className="courses-go" to="/javascript-table">
                                        <small>{t("courses.goToCourse")}</small>
                                        <span className="sr-only">JavaScript</span>
                                      </Link>
                                    </Col>
                                  </>


                                ) : (
                                  <Button className="courses-btn" color="primary" type="button" onClick={() => callRegisterToCourse("JS01")}>
                                    {t("courses.register")}
                                  </Button>
                                )}
                              </Row>

                            </CardBody>
                          </Col>
                        </Row>
                      </Card>
                    </Col>
                  </Row>

                  <Row data-reveal>
                    <Col >
                      <Card className="course-card">
                        <Row className="course-card-row">
                          <Col className="col-3 course-logo-col">
                            <Link to="/landing-page">
                              <CardImg className="course-logo" alt="Java" src={require("assets/img/icons/common/JavaCourseLogo.png")} />
                            </Link>
                          </Col>
                          <Col className="col-9 ">
                            <CardBody>
                              <CardTitle tag="h2">
                                Java
                              </CardTitle>
                              <CardText>
                                {t("courses.comingSoon")}
                              </CardText>
                              {/* <Button onClick={() => callRegisterToCourse("JA01")} color="primary" type="button">
                            Register
                          </Button> */}
                            </CardBody>
                          </Col>
                        </Row>
                      </Card>
                    </Col>
                  </Row>


                  <Row data-reveal>
                    <Col>
                      <Card className="course-card">
                        <Row className="course-card-row">
                          <Col className="col-3 course-logo-col">
                            <Link to="/landing-page">
                              <CardImg className="course-logo" alt="Python" src={require("assets/img/icons/common/PythonCourseLogo2.png")} />
                            </Link>
                          </Col>
                          <Col className="col-9">
                            <CardBody>
                              <CardTitle tag="h2">
                                Python
                              </CardTitle>

                              <CardText>
                                {t("courses.comingSoon")}
                              </CardText>

                              {/* CODE BELLOW FOR WHEN REGISTERING IS READY */}
                              {/* <Row className="courses-card-foot">
                                {isCourseRegistered("JS01") ? (
                                  <>
                                    <Col xs="6">
                                      <CardText color="success" className="courses-registered">
                                        <FaCheck className="courses-registered-icon" />
                                        Registered
                                      </CardText>
                                    </Col>
                                    <Col xs="6">
                                      <a
                                        className="courses-go"
                                        href=""
                                        onClick={() => navigate("/javascript-table")}
                                      >
                                        <small>Go to course</small>
                                      </a>
                                    </Col>
                                  </>
                                ) : (
                                  <Button className="courses-btn" color="primary" type="button" onClick={() => callRegisterToCourse("JS01")}>
                                    Register
                                  </Button>
                                )}
                              </Row> */}

                            </CardBody>
                          </Col>
                        </Row>
                      </Card>
                    </Col>
                  </Row>
                  </>)}
                  </div>

                </section>
              </TabPane>
              <TabPane tabId="2" role="tabpanel" id="courses-pane-mine" aria-labelledby={user ? "courses-tab-mine" : undefined}>

                {/* CODE BELLOW IS ANOTHER WAY TO DISPLAY IMAGES. DO NOT REMOVE UNTIL APPLICATION IS FULLED TESTED SINCE THE CURRENT METHOD IS UNSTABLE */}

                {/* <section className="section">
                  <span>test521</span>
                  {myCourses?.map((courseItem) => (

                    <div key={courseItem}>

                      {coursesLogosPaths[courseItem] && (
                        <Row>
                          <Col className="mb-5 mb-md-0" md="6">
                            <Card className="card-lift--hover shadow border-0">
                              <Link to="/landing-page">
                                <CardImg
                                  alt="..."
                                  src={coursesLogos(`./${coursesLogosPaths[courseItem]}`)}
                                />
                              </Link>
                            </Card>
                            <Progress color="primary" value={52} />
                          </Col>
                        </Row>
                      )
                      }
                    </div>
                  ))}
                </section> */}




                {/* THIS  METHOD COULD BE UNSTABLE FOR DISPLAYING IMAGES MAKE. WE NEED TO FULLY TEST IT BEFORE REMOVING THE ABOVE COMMENTED OUT CODE  */}
                <section className="courses-pane courses-mine">

                  {myCourses?.filter((courseItem) => SHOW_LEGACY_COURSES || courseItem === "CD01" || (showV2Course && courseItem === "CD02") || (showV3Course && courseItem === "CD03") || (showV4Course && courseItem === "CD04")).map((courseItem) => (

                    <div key={courseItem} className="courses-mine-item" data-reveal>

                      {coursesLogosFiles[courseItem] && (
                        <Row>
                          <Col className="courses-mine-col" md="6">
                            <Card className="courses-mine-card w-100">
                              <Link to={`/${coursesTableLinks[courseItem]}`}>
                                <CardImg
                                  className="courses-mine-img"
                                  alt={courseTitle(courseItem)}
                                  src={require(`assets/img/icons/common/${coursesLogosFiles[courseItem]}`)}
                                // onClick={() => navigate('/table-of-content')}
                                />
                              </Link>
                              <CardBody>

                                <Row className="courses-mine-foot">

                                  <Col xs="6">

                                  </Col>
                                  <Col xs="6">
                                    <Link className="courses-go" to={`/${coursesTableLinks[courseItem] || "javascript-table"}`}>
                                      <small>{t("courses.goToCourse")}</small>
                                      <span className="sr-only">{courseTitle(courseItem)}</span>
                                    </Link>
                                  </Col>




                                </Row>
                              </CardBody>
                            </Card>
                            {/* CODE BELLOW IS FOR ADDING PROGRESS BAR */}
                            {/* <Progress color="primary" value={52} /> */}
                          </Col>
                        </Row>
                      )
                      }

                    </div>

                  ))}


                </section>

              </TabPane>
            </TabContent>
          </Container>
        </section>

      </main>
      <MainFooter />

    </>
  );
};

export default Courses;
